import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronUp, ChevronsLeft, ChevronsRight, Smartphone } from 'lucide-react';
import { officeNav, sectionPath } from '@nu/shared';
import SyncStatus from '../components/SyncStatus.jsx';
import NavIcon from '../components/NavIcon.jsx';
import { signOut } from '../auth/msal.js';
import { useOnline, useOutboxCounts } from '../offline/index.js';
import { syncState } from '../offline/syncState.js';
import { useUnreadCount } from '../modules/comms/inbox.js';
import { useTasksDue } from '../modules/tasks/store.js';

// Desktop frame: everything in the sidebar (no top bar), like the NFUK
// platform. Each department (Operations, Platform...) is one link; it opens a
// page of cards for its screens, and stays highlighted while you're on any of
// them. At the bottom: the switch to the field view and your profile (which
// holds the save status and Sign out). Like NFUK, the sidebar collapses to a
// strip of icons for a wider page (remembered on this computer). Below 900px
// it becomes a slide-in menu, opened from a slim bar.
const RAIL_KEY = 'nu.sidebarRail';
export default function OfficeShell({ me, onSwitchShell }) {
  const { top, sections } = officeNav(me.capabilities);
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [rail, setRail] = useState(() => {
    try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
  });
  const toRail = (v) => {
    setRail(v);
    try { localStorage.setItem(RAIL_KEY, v ? '1' : '0'); } catch { /* this visit only */ }
  };
  const unread = useUnreadCount();
  const tasksDue = useTasksDue(); // due today or overdue
  // A department's screen (opened from its card) gets a way back to it. Pages
  // deeper in (a message, a new message) have their own back link.
  const from = sections.find((s) => s.items.some((i) => pathname === i.path));
  const within = (s) => pathname === sectionPath(s.id) || s.items.some((i) => pathname === i.path || pathname.startsWith(`${i.path}/`));

  const link = (i) => (
    <NavLink key={i.id} to={i.path} end={i.path === '/'} className="side-link" title={rail ? i.label : undefined} aria-label={rail ? i.label : undefined} onClick={() => setMenuOpen(false)}>
      <NavIcon name={i.icon} size={20} badge={i.id === 'messages' ? unread : i.id === 'tasks' ? tasksDue : 0} />
      <span>{i.label}</span>
    </NavLink>
  );

  return (
    <div className={`office-shell${menuOpen ? ' menu-open' : ''}${rail ? ' side-rail' : ''}`}>
      <aside className="sidebar">
        <img className="logo logo-full" src="/brand/nano-utilities-logo-white.svg" alt="Nano Utilities, part of Nano Fibre UK" />
        <img className="logo logo-badge" src="/brand/nano-utilities-badge.svg" alt="Nano Utilities" />
        <nav aria-label="Main">
          {top.map((i) => link(i))}
          {sections.length > 0 && <p className="side-label">Departments</p>}
          {sections.map((s) => (
            <NavLink key={s.id} to={sectionPath(s.id)} className={() => `side-link${within(s) ? ' active' : ''}`} title={rail ? s.label : undefined} aria-label={rail ? s.label : undefined} onClick={() => setMenuOpen(false)}>
              <NavIcon name={s.icon} size={20} />
              <span>{s.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="side-foot">
          {onSwitchShell && (
            <button type="button" className="side-link side-switch" title={rail ? 'Field view' : undefined} aria-label={rail ? 'Field view' : undefined} onClick={onSwitchShell}>
              <Smartphone size={20} aria-hidden="true" />
              <span>Field view</span>
            </button>
          )}
          <Profile me={me} />
          <button type="button" className="side-link side-switch side-toggle" title={rail ? 'Expand the menu' : 'Collapse the menu'} aria-label={rail ? 'Expand the menu' : 'Collapse the menu'} onClick={() => toRail(!rail)}>
            {rail ? <ChevronsRight size={20} aria-hidden="true" /> : <ChevronsLeft size={20} aria-hidden="true" />}
            <span>Collapse</span>
          </button>
        </div>
      </aside>
      <div className="office-body">
        <header className="topbar">
          <button type="button" className="icon-btn menu-btn" aria-label="Menu" onClick={() => setMenuOpen((o) => !o)}>☰</button>
          <SyncStatus />
        </header>
        <main className="office-main">
          {from && (
            <Link to={sectionPath(from.id)} className="link-btn back-link page-back">
              <ArrowLeft size={18} aria-hidden="true" /> {from.label}
            </Link>
          )}
          <Outlet />
        </main>
      </div>
      {menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} />}
    </div>
  );
}

// You: initials, name and email. Opens a small menu with the save status and
// Sign out. A dot on the initials when something is waiting or couldn't send.
function Profile({ me }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const online = useOnline();
  const { tone } = syncState({ online, ...useOutboxCounts() });
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  }, [open]);
  return (
    <div className="profile" ref={ref}>
      {open && (
        <div className="profile-menu" role="menu">
          <SyncStatus />
          <Link to="/profile" role="menuitem" className="profile-item" onClick={() => setOpen(false)}>My profile</Link>
          <button type="button" role="menuitem" className="profile-item" onClick={signOut}>Sign out</button>
        </div>
      )}
      <button type="button" className="profile-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="avatar" aria-hidden="true">
          {initials(me.name)}
          {tone !== 'ok' && <span className={`profile-dot profile-dot-${tone}`} />}
        </span>
        <span className="profile-who">
          <strong>{me.name}</strong>
          <span>{me.email}</span>
        </span>
        <ChevronUp size={18} aria-hidden="true" className={`profile-chev${open ? '' : ' closed'}`} />
      </button>
    </div>
  );
}

function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}
